import React, { useEffect, useRef, useState } from "react";
import "./addUsername.css";
import { createTransactionPin } from "../../actions/auth";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { initiateKycFlow, updatingKycDetails } from "../../actions/user";
import Vove, { VoveEnvironment } from "@vove-id/web-sdk";
import FormModal from "../FormModal";

const KycModal = () => {
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const closeButtonRef = useRef(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [kycStatus, setKycStatus] = useState(userDetails?.kyc_profile?.status);
  const [kycVerifiedAt, setKycVerifiedAt] = useState(userDetails?.kyc_profile?.last_verified_at);
  
  /** Load Dojah SDK dynamically */
  useEffect(() => {
    const scriptId = "dojah-sdk-script";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://widget.dojah.io/websdk.js";
      script.defer = true;
      script.onload = () => setSdkLoaded(true); 
      document.body.appendChild(script);
    } else {
      setSdkLoaded(true);
    }
  }, []);

  const openDojahKycWidget = async (data) => {
    if (!sdkLoaded || !window.Connect) {
      // console.error("Dojah SDK not loaded yet.");
      return;
    }

    const kycInitiation_credentials = {
      first_name: data.widget_config.user_data.first_name,
      last_name: data.widget_config.user_data.last_name,
      country: userDetails?.country,
      email: data.widget_config.user_data.email,
      country_code: data.widget_config.user_data.residence_country,
      date_of_birth: data.widget_config.user_data.date_of_birth,
      ...(userDetails?.account_type === "business" && { business_name: userDetails?.business_name }),
    };

    const options = {
      app_id: data.widget_config.app_id,
      p_key: data.widget_config.public_key,
      type: "custom",
      user_data: {
        first_name: kycInitiation_credentials.first_name,
        last_name: kycInitiation_credentials.last_name,
        dob: kycInitiation_credentials.date_of_birth,
        residence_country: kycInitiation_credentials.country_code,
        email: kycInitiation_credentials.email,
      },
      metadata: { user_id: userDetails?.id, kyc_verification_id: data.kyc_verification_record.reference_id},
      config: { widget_id: data.widget_config.widget_id, webhook : true },
      onSuccess: async (response) => {
        const credential = {reference_id: data.kyc_verification_record.reference_id}
        await updatingKycDetails(credential, dispatch, toast);
        setKycStatus("pending");
      },
      onError: (err) => console.error("Error:", err),
      onClose: () => console.log("User closed the widget"),
    };

    const connect = new window.Connect(options);
    connect.setup();
    connect.open();
  };

  const initializeVoveIdWidget = async (kycRecord) => {
    const vove = new Vove();
    const publicKey = process.env.REACT_APP_VOVE_ID_PUBLIC_KEY; 
    vove.start({
      sessionToken: kycRecord.kycSessionToken,
      environment: kycRecord.env == 'sandbox' ? VoveEnvironment.SANDBOX : VoveEnvironment.PRODUCTION,
      publicKey: publicKey,
      onVerificationComplete: (status) => {
        switch (status) {
          case 'success':
            toast.success("KYC verification successful!");
            // console.log('Verification successful.');
            break;
          case 'pending':
            toast.info("KYC verification is pending. We will notify you once it's completed.");
            // console.log('Verification pending.');
            break;
          case 'canceled':
            toast.warn("KYC verification was canceled. Please try again.");
            // console.log('Verification canceled.');
            break;
          default:
            toast.error("An unexpected error occurred during KYC verification. Please try again.");
            // console.log('Unexpected status:', status);
        }
      }
    })
  }

  const handleKycDetailsSubmission = async (data) => {
    const kycProvider = data.kyc_verification_record.provider;
    if(kycProvider == 'dojah'){
      openDojahKycWidget(data);
    }
    else if(kycProvider == 'vove_id'){
      initializeVoveIdWidget(data.kyc_verification_record);
    }
    else if(kycProvider == 'anchor'){
      const credential = {reference_id: data.kyc_verification_record.reference_id}
      await updatingKycDetails(credential, dispatch, toast);
      setKycStatus("pending");
    }
  }

  return (
    <>
      <ManualKycModal
        kycModalId="kycModal"
        userDetails={userDetails}
        handleKycInitiation={handleKycDetailsSubmission}
      />
    </>
  );
};

export const ManualKycModal = ({userDetails, handleKycInitiation, kycModalId}) => {
  const [loading, setLoading] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const userIsNigerian = userDetails?.country && userDetails?.country.toLowerCase() == 'nigeria';

  const [form, setForm] = useState({
    first_name: userDetails?.first_name,
    last_name: userDetails?.last_name,
    date_of_birth: userDetails?.date_of_birth ?? "",
    gender: userDetails?.gender ?? "",
  });

  const closeBtnRef = useRef(null);

  const submitKyc = async () => {
    setLoading(true);
    try {
      const credentials = {
        first_name: form.first_name,
        last_name: form.last_name,
        date_of_birth: form.date_of_birth,
        gender: form.gender,
      };
      const responseData = await initiateKycFlow(credentials);
      if(responseData.status == 'success' && responseData.data.proceed == true){
        handleKycInitiation(responseData.data);
        closeBtnRef.current?.click();
        setLoading(false);
      }
      else{
        toast.error(responseData.message);
      }
    } 
    catch (err) {
      toast.error("An error occurred while submitting your KYC details. Please try again.");
      console.error(err);
    } 
    finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setIsEdited(true);
    setForm({...form, [e.target.name]: e.target.value});
  };

  useEffect(() => {
    const firstNameIsEdited = form.first_name ? form.first_name.length > 0 : false;
    const lastNameIsEdited = form.last_name ? form.last_name.length > 0 : false;
    const dateOfBirthIsEdited = form.date_of_birth ? form.date_of_birth.length > 0 : false;
    const genderIsEdited = form.gender ? form.gender.length > 0 : false;
    if(firstNameIsEdited || lastNameIsEdited || dateOfBirthIsEdited || genderIsEdited) {
      setIsEdited(true)
    }
  }, []);
  return (
    <>
      <FormModal id={kycModalId} 
        title="KYC Verification" 
        onSubmit={submitKyc} 
        loading={loading} 
        setLoading={setLoading} 
        isEdited={isEdited} 
        btnText="Proceed" 
        processingText="Processing"
        closeBtnRef={closeBtnRef}
      >
        <div className="mb-4">
          <label className="form-label">First Name</label>
          <input className="form-control" name="first_name" value={form.first_name} onChange={handleChange} required/>
        </div>

        <div className="mb-4">
          <label className="form-label">Last Name</label>
          <input className="form-control" name="last_name" value={form.last_name} onChange={handleChange} required/>
        </div>

        <div className="mb-4">
          <label className="form-label">Date of Birth</label>
          <input type="date" className="form-control" name="date_of_birth" value={form.date_of_birth} onChange={handleChange} required/>
        </div>

        <div className="mb-4">
          <label className="form-label">Gender</label>
          <select className="form-control" name="gender" value={form.gender} onChange={handleChange} required>
            <option value="">Select your gender </option>
            <option value="male"> Male </option>
            <option value="female"> Female </option>
          </select>
        </div>
      </FormModal>
    </>
  );
};

export default KycModal;